import { Banknote, CreditCard, Landmark, Wallet } from "lucide-react";
import { AccountType, CardNetwork } from "@boo/shared";
import type { ThaiBank } from "@boo/shared";
import { bankLogoInfo, networkInfo } from "../lib/bankLogos";

type Size = "sm" | "md";

interface AccountAvatarProps {
  type: AccountType;
  bankCode: ThaiBank | null | undefined;
  cardNetwork?: CardNetwork | null;
  size?: Size;
}

const TYPE_ICONS = {
  [AccountType.Bank]: Landmark,
  [AccountType.CreditCard]: CreditCard,
  [AccountType.Cash]: Banknote,
  [AccountType.EWallet]: Wallet,
};

const BOX: Record<Size, string> = {
  sm: "size-4 rounded",
  md: "size-10 rounded-xl",
};

/** Bank logo (or a type icon when none is known); credit cards add a network badge. */
export default function AccountAvatar({
  type,
  bankCode,
  cardNetwork,
  size = "md",
}: AccountAvatarProps) {
  const bank = bankLogoInfo(bankCode);
  const network =
    type === AccountType.CreditCard ? networkInfo(cardNetwork) : null;
  const TypeIcon = TYPE_ICONS[type];

  return (
    <span className="relative inline-flex shrink-0">
      {bank ? (
        <img
          src={`/logos/banks/${bank.file}`}
          alt={bank.label}
          className={`${BOX[size]} object-cover`}
          loading="lazy"
        />
      ) : (
        <span
          className={`${BOX[size]} inline-flex items-center justify-center bg-[var(--surface-muted)] text-muted-foreground`}
        >
          <TypeIcon className={size === "sm" ? "size-3" : "size-5"} />
        </span>
      )}
      {network && size === "md" && (
        <span
          title={network.label}
          className="absolute -bottom-1 -right-1 inline-flex h-4 min-w-6 items-center justify-center rounded bg-white px-0.5 ring-1 ring-border"
        >
          {network.icon ? (
            <svg
              viewBox="0 0 24 24"
              role="img"
              aria-label={network.label}
              className="h-3 w-5"
              fill={`#${network.icon.hex}`}
            >
              <path d={network.icon.path} />
            </svg>
          ) : (
            <span className="text-[7px] font-bold text-neutral-700">UP</span>
          )}
        </span>
      )}
    </span>
  );
}
